import { canAccessModule, DOCUMENTS_ONLY_MESSAGE, isStaffRole, MODULE_LABELS, MODULES, NO_SERVICE_MESSAGE, portalNeedsFullAccess, staffServicesFor, type Module } from "@clientflow/shared";
import { BarChart3, Briefcase, CalendarCheck, CalendarDays, Calculator, FileText, Home, Landmark, LayoutDashboard, LineChart, MessageSquare, Receipt, Settings, Sparkles, Users, UserCog, Wallet } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Redirect, Route, Switch, useLocation } from "wouter";
import { Loading } from "@/components/page";
import { Shell, type NavGroup } from "@/components/shell";
import { seesDashboard, useMe, type Me } from "@/lib/session";
import { AcceptInvitePage, FirmPickerPage, ForgotPasswordPage, Invitations, LoginPage, MfaSetup, MfaVerifyPage, NoAccessPage, ResetPasswordPage } from "@/pages/auth";
import { PortalDocumentsPage, PortalHomePage, PortalMessagesPage, ProfilePage, usePortalHome } from "@/pages/portal";
import { PortalFeeApprovalPage, PortalInvoicePage, PortalInvoicesPage, PortalOrganizerPage, PortalRefundsPage, PortalSignPage, usePortalTodos } from "@/pages/portal-tax";
import { ClientDetailPage } from "@/pages/staff/client-detail";
import { ClientsPage } from "@/pages/staff/clients";
import { InboxPage, SettingsPage } from "@/pages/staff/inbox-settings";
import { MyDayPage } from "@/pages/staff/my-day";
import { EngagementPage } from "@/pages/staff/work";
import { TaxOrganizersPage } from "@/pages/staff/season";
import { ProposalPage } from "@/components/lifecycle";
import { PortalProposalPage, ReviewPreferencesPage } from "@/pages/portal-lifecycle";
import { TaxInputPage } from "@/pages/staff/tax-input";
import { DeadlinesPage } from "@/pages/staff/deadlines";
import { ComplianceFilingsPage } from "@/components/entity-compliance";
import { TaxEstimatesPage, TaxNoticesPage } from "@/components/estimates-notices";
import { PortalTaxPaymentsPage } from "@/pages/portal-estimates";
import { TaxPlanningPage, TaxPlanPage } from "@/pages/staff/tax-planning";
import { PortalTaxPlanPage } from "@/pages/portal-tax-plan";
import { PayrollChangesPage, PayrollFilingsPage } from "@/components/payroll";
import { PortalPayrollPage } from "@/pages/portal-payroll";
import { IntakeFormsPage, OnboardingDetailPage, OnboardingPage } from "@/pages/staff/onboarding";
import { PortalBooksPage, QuestionLinkPage } from "@/pages/portal-books";
import { PortalCfoPage } from "@/pages/portal-cfo";
import { LandingPage } from "@/pages/landing";
import { BookingManagePage, PublicBookingPage } from "@/pages/book-public";
import { PublicInquiryPage } from "@/pages/inquire-public";
import { LeadsPage } from "@/pages/staff/leads";
import { OAuthConsentPage } from "@/pages/oauth-consent";
import { PortalBookPage } from "@/pages/portal-book";
import { MeetingsPage } from "@/pages/staff/meetings";
import { AnalyticsPage } from "@/pages/staff/analytics";
import { AccountingServicePage, AdvisoryServicePage, BillingServicePage, PayrollServicePage, ReviewPage, TaxServicePage } from "@/pages/staff/service-pages";
import { RunningTimer } from "@/components/time-tracker";

const MODULE_ICONS = { tax: Calculator, bookkeeping: Briefcase, payroll: Wallet, advisory: LineChart } as const;

/** Each service is one menu item; its sections appear beneath it while you are inside it. */
const SERVICE_NAV: Record<Module, { href: string; children?: Array<{ href: string; label: string }> }> = {
  tax: {
    href: "/tax",
    children: [
      // Opens the Overview tab for people who see the tax dashboard, the Returns tab for everyone else.
      { href: "/tax", label: "Overview & Returns" },
      { href: "/tax/organizers", label: "Organizers" },
      { href: "/tax/planning", label: "Tax Planning" },
      { href: "/tax/estimates", label: "Estimated Payments" },
      { href: "/tax/notices", label: "IRS & State Notices" },
      { href: "/tax/compliance", label: "Entity & Sales Tax" },
    ],
  },
  bookkeeping: { href: "/accounting" },
  payroll: {
    href: "/payroll",
    children: [
      { href: "/payroll", label: "Payroll" },
      { href: "/payroll/changes", label: "Changes & New Hires" },
      { href: "/payroll/filings", label: "Payroll Filings" },
    ],
  },
  advisory: { href: "/advisory" },
};

/** Addresses used before the services were reorganized, so bookmarks keep working. */
const MOVED: Array<[string, string]> = [
  ["/work/tax", "/tax/returns"],
  ["/work/bookkeeping", "/accounting/workflow"],
  ["/work/payroll", "/payroll/workflow"],
  ["/work/advisory", "/advisory/workflow"],
  ["/tax-tools", "/tax/extensions"],
  ["/close", "/accounting"],
  ["/inbox", "/messages"],
];

function StaffApp({ me }: { me: Me }) {
  const modules = MODULES.filter((m) => canAccessModule(me.role, me.staffModules, m));
  const has = (m: Module) => modules.includes(m);
  const firmName = me.memberships?.find((m) => m.firmId === me.activeFirmId)?.firmName;
  const { data: ai } = useQuery<{ pending: number }>({ queryKey: ["/api/ai/proposals/count"], refetchInterval: 60_000 });
  const docs = useQuery<{ review: number }>({ queryKey: ["/api/document-reads/count"], refetchInterval: 60_000, enabled: !!me.permissions?.includes("documents.read") });
  const nav: NavGroup[] = [
    {
      items: [
        { href: "/", label: "Dashboard", icon: LayoutDashboard },
        {
          href: "/clients",
          label: "Clients",
          icon: Users,
          children: [
            { href: "/clients", label: "All Clients" },
            { href: "/clients/leads", label: "Leads" },
            { href: "/clients/onboarding", label: "Onboarding" },
            { href: "/clients/intake-forms", label: "Intake Forms" },
          ],
        },
        { href: "/messages", label: "Messages", icon: MessageSquare },
        { href: "/meetings", label: "Meetings", icon: CalendarCheck },
        { href: "/ai", also: ["/documents/review"], label: "Review", icon: Sparkles, badge: (ai?.pending ?? 0) + (docs.data?.review ?? 0) || undefined },
        ...(has("tax") || has("payroll") || has("bookkeeping") ? [{ href: "/deadlines", label: "Deadlines", icon: CalendarDays }] : []),
        // Without Tax, the firm's compliance list sits here rather than under Tax.
        ...(!has("tax") && has("bookkeeping") ? [{ href: "/tax/compliance", label: "Entity & Sales Tax", icon: Landmark }] : []),
      ],
    },
    {
      label: "Services",
      items: modules.map((m) => ({ href: SERVICE_NAV[m].href, label: MODULE_LABELS[m], icon: MODULE_ICONS[m], children: SERVICE_NAV[m].children?.map((c) => (c.href === "/tax" && !seesDashboard(me, "tax") ? { ...c, label: "Returns" } : c)) })),
    },
    {
      label: "Firm",
      items: [
        ...(me.permissions?.includes("analytics.read") ? [{ href: "/analytics", label: "Firm Analytics", icon: BarChart3 }] : []),
        ...(me.permissions?.includes("billing.read") ? [{ href: "/billing", label: "Billing", icon: Receipt }] : []),
        { href: "/settings", label: "Firm Settings", icon: Settings },
      ],
    },
  ];
  return (
    <Shell me={me} nav={nav} firmName={firmName} floating={<RunningTimer />}>
      <Switch>
        <Route path="/">{() => <MyDayPage me={me} />}</Route>
        {me.permissions?.includes("analytics.read") && <Route path="/analytics">{() => <AnalyticsPage page="overview" me={me} />}</Route>}
        {me.permissions?.includes("analytics.read") && <Route path="/analytics/:page">{(p) => <AnalyticsPage page={p.page} me={me} />}</Route>}
        <Route path="/clients">{() => <ClientsPage me={me} />}</Route>
        <Route path="/clients/leads">{() => <LeadsPage me={me} />}</Route>
        <Route path="/clients/onboarding">{() => <OnboardingPage me={me} />}</Route>
        <Route path="/clients/onboarding/:id">{(p) => <OnboardingDetailPage key={p.id} id={p.id} me={me} />}</Route>
        <Route path="/clients/intake-forms">{() => <IntakeFormsPage me={me} />}</Route>
        {has("tax") && <Route path="/clients/:id/tax-input">{(p) => <TaxInputPage key={p.id} clientId={p.id} />}</Route>}
        <Route path="/clients/:id">{(p) => <ClientDetailPage key={p.id} id={p.id} me={me} />}</Route>
        <Route path="/engagements/:id">{(p) => <EngagementPage key={p.id} id={p.id} me={me} />}</Route>
        <Route path="/meetings">{() => <MeetingsPage me={me} />}</Route>
        <Route path="/ai">{() => <ReviewPage me={me} tab="ai" counts={{ ai: ai?.pending, documents: docs.data?.review }} />}</Route>
        <Route path="/documents/review">{() => <ReviewPage me={me} tab="documents" counts={{ ai: ai?.pending, documents: docs.data?.review }} />}</Route>
        {has("tax") && <Route path="/tax">{() => <TaxServicePage me={me} tab="overview" />}</Route>}
        {has("tax") && <Route path="/tax/returns">{() => <TaxServicePage me={me} tab="returns" />}</Route>}
        {has("tax") && <Route path="/tax/organizers">{() => <TaxOrganizersPage me={me} />}</Route>}
        {has("tax") && <Route path="/tax/extensions">{() => <TaxServicePage me={me} tab="extensions" />}</Route>}
        {has("tax") && <Route path="/tax/refunds">{() => <TaxServicePage me={me} tab="refunds" />}</Route>}
        {has("tax") && <Route path="/tax/planning">{() => <TaxPlanningPage me={me} />}</Route>}
        {has("tax") && <Route path="/tax/planning/:id">{(p) => <TaxPlanPage key={p.id} id={p.id} me={me} />}</Route>}
        {has("tax") && <Route path="/tax/estimates">{() => <TaxEstimatesPage />}</Route>}
        {has("tax") && <Route path="/tax/notices">{() => <TaxNoticesPage me={me} />}</Route>}
        {(has("tax") || has("bookkeeping")) && <Route path="/tax/compliance">{() => <ComplianceFilingsPage me={me} />}</Route>}
        {(has("tax") || has("payroll") || has("bookkeeping")) && <Route path="/deadlines">{() => <DeadlinesPage me={me} />}</Route>}
        {has("bookkeeping") && <Route path="/accounting">{() => <AccountingServicePage me={me} tab="overview" />}</Route>}
        {has("bookkeeping") && <Route path="/accounting/workflow">{() => <AccountingServicePage me={me} tab="workflow" />}</Route>}
        {has("payroll") && <Route path="/payroll">{() => <PayrollServicePage me={me} tab="overview" />}</Route>}
        {has("payroll") && <Route path="/payroll/changes">{() => <PayrollChangesPage me={me} />}</Route>}
        {has("payroll") && <Route path="/payroll/filings">{() => <PayrollFilingsPage me={me} />}</Route>}
        {has("payroll") && <Route path="/payroll/workflow">{() => <PayrollServicePage me={me} tab="workflow" />}</Route>}
        {has("advisory") && <Route path="/advisory">{() => <AdvisoryServicePage me={me} tab="overview" />}</Route>}
        {has("advisory") && <Route path="/advisory/workflow">{() => <AdvisoryServicePage me={me} tab="workflow" />}</Route>}
        <Route path="/messages">{() => <InboxPage me={me} />}</Route>
        <Route path="/billing">{() => <BillingServicePage me={me} tab="invoices" />}</Route>
        <Route path="/billing/recurring">{() => <BillingServicePage me={me} tab="recurring" />}</Route>
        <Route path="/billing/proposals">{() => <BillingServicePage me={me} tab="proposals" />}</Route>
        <Route path="/billing/proposals/new">{() => <ProposalPage id={null} me={me} clientId={new URLSearchParams(window.location.search).get("clientId")} />}</Route>
        <Route path="/billing/proposals/:id">{(p) => <ProposalPage key={p.id} id={p.id} me={me} />}</Route>
        <Route path="/settings">{() => <SettingsPage me={me} />}</Route>
        <Route path="/profile">{() => <ProfilePage me={me} />}</Route>
        {MOVED.map(([from, to]) => (
          <Route key={from} path={from}>
            <Redirect to={to} replace />
          </Route>
        ))}
        <Route path="/sign-in">
          <Redirect to="/" replace />
        </Route>
        <Route>{() => <StaffFallback modules={modules} />}</Route>
      </Switch>
    </Shell>
  );
}

function PortalApp({ me }: { me: Me }) {
  const { data } = usePortalHome();
  const todos = usePortalTodos();
  const firmName = me.memberships?.find((m) => m.firmId === me.activeFirmId)?.firmName;
  // Documents-only access sees documents and nothing else: invoices, messages, reports and
  // payroll belong to the people with full access.
  const fullClients = (data?.clients ?? []).filter((c) => c.access === "full");
  const full = fullClients.length > 0;
  // A dashboard the firm keeps out of a client's portal hides only its figures; the page stays for the client's own work.
  const hasBooks = fullClients.some((c) => c.services.some((s) => s.module === "bookkeeping"));
  const hasCfo = fullClients.some((c) => c.services.some((s) => s.module === "advisory"));
  const hasTax = fullClients.some((c) => c.services.some((s) => s.module === "tax"));
  const hasPayroll = fullClients.some((c) => c.services.some((s) => s.module === "payroll"));
  const nav: NavGroup[] = [
    {
      items: [
        { href: "/", label: "Overview", icon: Home, badge: (data?.actions.length ?? 0) + todos.count || undefined },
        { href: "/documents", label: "Documents", icon: FileText },
        ...(full ? [{ href: "/invoices", label: "Invoices & Payments", icon: Receipt }] : []),
        ...(full ? [{ href: "/book", label: "Book a Meeting", icon: CalendarCheck }] : []),
        ...(full ? [{ href: "/refunds", label: "Refund Status", icon: Landmark }] : []),
        ...(hasTax ? [{ href: "/tax-payments", label: "Tax Payments & Letters", icon: Calculator }] : []),
        ...(hasTax ? [{ href: "/tax-plan", label: "Tax Plan", icon: LineChart }] : []),
        ...(hasBooks ? [{ href: "/books", label: "Financial Reports", icon: Briefcase }] : []),
        ...(hasCfo ? [{ href: "/cfo", label: "Performance Dashboard", icon: LineChart }] : []),
        ...(hasPayroll ? [{ href: "/payroll", label: "Payroll", icon: Wallet }] : []),
        ...(full ? [{ href: "/messages", label: "Messages", icon: MessageSquare, badge: data?.unreadThreads }] : []),
        { href: "/profile", label: "Account & Security", icon: UserCog },
      ],
    },
  ];
  return (
    <Shell me={me} nav={nav} firmName={firmName}>
      <Switch>
        <Route path="/">{() => <PortalHomePage me={me} />}</Route>
        <Route path="/documents">{() => <PortalDocumentsPage />}</Route>
        {(!data || full) && <Route path="/invoices">{() => <PortalInvoicesPage />}</Route>}
        {(!data || full) && <Route path="/invoices/:id">{(p) => <PortalInvoicePage key={p.id} id={p.id} />}</Route>}
        <Route path="/proposals/:id">{(p) => <PortalProposalPage key={p.id} id={p.id} />}</Route>
        <Route path="/sign/:id">{(p) => <PortalSignPage key={p.id} id={p.id} />}</Route>
        <Route path="/fees/:id">{(p) => <PortalFeeApprovalPage key={p.id} id={p.id} />}</Route>
        <Route path="/organizers/:id">{(p) => <PortalOrganizerPage key={p.id} id={p.id} />}</Route>
        {(!data || full) && <Route path="/refunds">{() => <PortalRefundsPage />}</Route>}
        {(!data || full) && <Route path="/book">{() => <PortalBookPage />}</Route>}
        {(!data || full) && <Route path="/payroll">{() => <PortalPayrollPage clientName={(id) => data?.clients.find((c) => c.id === id)?.displayName} />}</Route>}
        {(!data || full) && <Route path="/tax-plan">{() => <PortalTaxPlanPage />}</Route>}
        {(!data || full) && <Route path="/tax-payments">{() => <PortalTaxPaymentsPage clients={fullClients} />}</Route>}
        {(!data || full) && <Route path="/cfo">{() => <PortalCfoPage clientName={(id) => data?.clients.find((c) => c.id === id)?.displayName} />}</Route>}
        {(!data || full) && <Route path="/books">{() => <PortalBooksPage clientName={(id) => data?.clients.find((c) => c.id === id)?.displayName} />}</Route>}
        {(!data || full) && <Route path="/messages">{() => <PortalMessagesPage me={me} />}</Route>}
        <Route path="/profile">{() => <ProfilePage me={me} portal />}</Route>
        <Route path="/sign-in">
          <Redirect to="/" replace />
        </Route>
        <Route>{() => <PortalFallback loaded={!!data} full={full} />}</Route>
      </Switch>
    </Shell>
  );
}

export function App() {
  const { data: me, isLoading, error, refetch } = useMe();
  if (window.location.pathname === "/accept-invite") return <AcceptInvitePage />;
  if (window.location.pathname === "/forgot-password") return <ForgotPasswordPage />;
  if (window.location.pathname === "/reset-password") return <ResetPasswordPage />;
  if (window.location.pathname === "/q") return <QuestionLinkPage />;
  if (window.location.pathname === "/review-preferences") return <ReviewPreferencesPage />;
  // The public booking page and its manage links work for anyone, signed in or not.
  if (window.location.pathname === "/book/manage") return <BookingManagePage />;
  const bookSlug = /^\/book\/([a-z0-9-]+)\/?$/i.exec(window.location.pathname)?.[1];
  if (bookSlug) return <PublicBookingPage slug={bookSlug} />;
  const inquireSlug = /^\/inquire\/([a-z0-9-]+)\/?$/i.exec(window.location.pathname)?.[1];
  if (inquireSlug) return <PublicInquiryPage slug={inquireSlug} />;
  if (error && !me) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 px-4 text-center">
        <p className="font-medium">ClientFlow is not responding.</p>
        <p className="text-sm text-muted-foreground">Check your internet connection, then try again.</p>
        <button className="rounded-md border border-border px-4 py-2 text-sm hover:bg-muted" onClick={() => refetch()}>
          Try again
        </button>
      </div>
    );
  }
  if (isLoading || !me) return <Loading />;
  // Signed out: the home address shows the public landing page; every other address
  // (including /sign-in and deep links into the app) shows the sign-in form.
  if (!me.user) return window.location.pathname === "/" ? <LandingPage /> : <LoginPage />;
  if (me.user.mfaEnabled && !me.mfaVerified) return <MfaVerifyPage />;
  if (!me.activeFirmId) return (me.memberships?.length ?? 0) > 1 ? <FirmPickerPage me={me} /> : <NoAccessPage me={me} />;
  if (isStaffRole(me.role) && !me.user.mfaEnabled) return <MfaSetup />;
  // Claude asking to connect: only staff can approve it.
  if (window.location.pathname === "/oauth/authorize" && isStaffRole(me.role)) return <OAuthConsentPage />;
  return me.role === "client" ? <PortalApp me={me} /> : <StaffApp me={me} />;
}

function NoAccess({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-20 text-center">
      <h1 className="text-2xl font-semibold">{title}</h1>
      <p className="text-sm text-muted-foreground">{children}</p>
      <Link href="/" className="mt-2 text-sm font-medium text-primary hover:underline">
        Back to the overview
      </Link>
    </div>
  );
}

/** A service address the person does not have: say so, and who can change it, instead of "Page not found". */
function StaffFallback({ modules }: { modules: Module[] }) {
  const [path] = useLocation();
  const needs = staffServicesFor(path);
  if (needs && !needs.some((m) => modules.includes(m))) return <NoAccess title={NO_SERVICE_MESSAGE}>Ask a firm owner or administrator to add it to your account in Firm Settings.</NoAccess>;
  return <NotFound />;
}

/** Documents-only access: the pages that belong to full access explain that, rather than showing nothing. */
function PortalFallback({ loaded, full }: { loaded: boolean; full: boolean }) {
  const [path] = useLocation();
  if (loaded && !full && portalNeedsFullAccess(path)) return <NoAccess title="Documents only">{DOCUMENTS_ONLY_MESSAGE}</NoAccess>;
  return <NotFound />;
}

function NotFound() {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center gap-3 py-20 text-center">
      <p className="text-sm font-medium text-primary">404</p>
      <h1 className="text-2xl font-semibold">Page not found</h1>
      <p className="text-sm text-muted-foreground">The link may be out of date, or the page has moved.</p>
      <Link href="/" className="mt-2 text-sm font-medium text-primary hover:underline">
        Back to the overview
      </Link>
    </div>
  );
}
